import { Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Host,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatCheckbox } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { ActivityLogService } from '@klickdata/core/activity-log';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { GlobalSearchData } from '@klickdata/core/global-search';
import { MessageDeletedComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import {
    AppScope,
    LiveEditorFieldLockService,
    Resource,
    ResourceCategory,
    ResourceData,
    ResourceService,
    ResourceTag,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceActionHandlerService } from '@klickdata/core/resource/src/resource-action-handler.service';
import { ResourcePublicationService } from '@klickdata/core/resource/src/resource-publication.service';
import { ResourceSocketService } from '@klickdata/core/resource/src/resource-ws.service';
import { Currency } from '@klickdata/core/resource/src/resource.model';
import { ScormService } from '@klickdata/core/scorm';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { VTQ, VTQService } from '@klickdata/core/vtq';
import { CategoryChipSelectComponent } from '@klickdata/shared-components/src/chip-selection/category-chip-select/category-chip-select.component';
import { TagChipSelectComponent } from '@klickdata/shared-components/src/tag-chip-select/tag-chip-select.component';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { EMPTY, Observable, Subject, merge } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ResourceStaffRoles } from '../../../../core/resource/src/types.enum';
import { ResourceAvailabilitySheetComponent } from '../../resource-assignment/resource-availability-sheet/resource-availability-sheet.component';
import { ResourceBuilderComponent } from '../../resource-builder/resource-builder.component';
import { ResourceListingSheetComponent } from '../../resource-listing-sheet/resource-listing-sheet.component';
import { ResourceManagerEvent } from '../resource-manager-event';
import { ResourceManagementComponent } from '../resource-manager.component';

@Component({
    selector: 'app-resource-manager-controls',
    templateUrl: './resource-manager-controls.component.html',
    styleUrls: ['./resource-manager-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ResourceManagerControlsComponent implements OnInit, OnChanges, OnDestroy {
    public resourceForm: FormGroup;
    @Input() resource: Resource | null;
    @Input() typeId: ResourceTypes;
    @Input() set resourceBuilder(value: ResourceBuilderComponent | null) {
        if (value) {
            this._resourceBuilder = value;
            merge(value.getCurrentForm().valueChanges, value.getLoading().pipe(filter((loading) => !loading)))
                .pipe(takeUntil(this.destroy))
                .subscribe(() => this.cdRef.markForCheck());
        }
    }

    private _resourceBuilder: ResourceBuilderComponent;
    public get resourceBuilder(): ResourceBuilderComponent {
        return this._resourceBuilder;
    }
    ResourceTypes = ResourceTypes;
    @ViewChild('publishCheckbox') publishCheckbox: MatCheckbox;
    @ViewChild('resourceEditors') resourceEditors: TemplateRef<any>;
    @ViewChild('scormPackageInfo') scormPackageInfo: TemplateRef<any>;
    public destroy: Subject<boolean> = new Subject<boolean>();
    @Input() public resKeysValidaty: { [key: string]: string | boolean }[];
    @ViewChild(CategoryChipSelectComponent) categoriesComp: CategoryChipSelectComponent;
    @ViewChild(TagChipSelectComponent) tagsComp: TagChipSelectComponent;
    @Input() public loading: boolean;
    // public loadingUserManager: boolean;
    public defaultResourceOnlineEventId: number;
    public onFormReset: Subject<boolean> = new Subject<boolean>();
    public resourceHasItemsSubject: Subject<boolean> = new Subject<boolean>();
    public accessLevel: 'all' | 'partial' | 'none' = 'none';
    @Input() user: User;
    @Input() customer: Customer;
    ResourceStaffRoles = ResourceStaffRoles;
    public resSubmitDisabledStatus$: Observable<boolean>;
    public vTQ$: Observable<VTQ>;
    public currencies: Currency[];
    public currentResourceEditors$: Observable<User[]>;
    private readonly liveEditorFieldLock = inject(LiveEditorFieldLockService);

    constructor(
        @Host() protected host: ResourceManagementComponent,
        protected parentFormDirective: FormGroupDirective,
        protected bottomSheet: MatBottomSheet,
        protected message: MessageService,
        protected cdRef: ChangeDetectorRef,
        protected mobileService: MobileService,
        protected resourceService: ResourceService,
        protected location: Location,
        protected downloadHelper: DownloadHelperService,
        protected configService: ConfigService,
        protected router: Router,
        protected activityLogService: ActivityLogService,
        protected vTQService: VTQService,
        protected auth: AuthService,
        protected scormService: ScormService,
        protected resourcePublicationService: ResourcePublicationService,
        protected resourceActionHandlerService: ResourceActionHandlerService,
        protected resourceSocketService: ResourceSocketService
    ) {
        this.defaultResourceOnlineEventId = this.configService.config.defaultResourceOnlineEventId;
        this.currencies = Utils.getAllCurrencies();
    }

    ngOnInit(): void {
        this.resourceForm = this.parentFormDirective.form;
        this.resSubmitDisabledStatus$ = this.resourceService.getResSubmitDisabled();
        this.vTQ$ = this.vTQService.getActiveVTQ();
    }

    /**
     * The items list is only trustworthy once the user has edited it. Until then it may still be
     * loading into the form, so the saved resource decides, otherwise opening a published resource
     * would silently unpublish it before its items arrive.
     */
    private hasItems(formValue: any): boolean {
        if (!ResourceTypes.isTypeThatHasItems(this.typeId)) {
            return true;
        }
        const items = this.resourceBuilder?.getCurrentForm()?.get('items') ?? this.resourceForm.get('resource_items');
        const formHasItems =
            !!items?.value?.length || !!formValue?.resource_items?.length || !!formValue?.items?.length;
        return items?.dirty ? formHasItems : formHasItems || !!this.resource?.items_attached;
    }
    // setUpAutoSave() {
    //     this.resourceForm.valueChanges
    //         .pipe(
    //             takeUntil(this.destroy),
    //             debounceTime(5000),
    //             filter(() => this.resourceForm.dirty && this.resourceForm.valid)
    //         )
    //         .subscribe(() => {
    //             this.parentFormDirective.ngSubmit.emit(ResourceManagerEvent.SUBMIT);
    //         });
    // }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.resource && changes.resource.currentValue) {
            this.handleAccessLevelIndicator(this.resource);
            this.handleCurrentResourceEditors();
            this.resourceHasItemsSubject.next(
                (ResourceTypes.isTypeThatHasItems(this.typeId) && this.resource.items_attached) ||
                    !ResourceTypes.isTypeThatHasItems(this.typeId)
            );
        }

        if (changes.resKeysValidaty) {
            this.resKeysValidaty = changes.resKeysValidaty.currentValue;
            this.cdRef.markForCheck();
        }

        if (changes.loading) {
            this.cdRef.markForCheck();
        }
        if (!!this.resourceForm && !!this.resourceForm.get('items')) {
            this.resourceForm
                .get('items')
                .valueChanges.pipe(takeUntil(this.destroy))
                .subscribe((value) => {
                    const hasItems = this.hasItems(value);
                    // Guarded on the current value, otherwise the emitted reset re-enters this subscriber.
                    if (!hasItems && value?.publish) {
                        this.resourceForm.patchValue({ publish: false });
                    }
                    this.resourceHasItemsSubject.next(hasItems);
                });
        }
    }

    public handleCurrentResourceEditors() {
        if (!!this.resource?.id) {
            this.currentResourceEditors$ = this.resourceSocketService
                .listenToCurrentResourceEditors(this.resource.id)
                .pipe(
                    tap((editors) => {
                        this.liveEditorFieldLock.applyEditors(editors);
                        this.cdRef.markForCheck();
                    }),
                    takeUntil(this.destroy),
                    shareReplay({ bufferSize: 1, refCount: true })
                );
        }
    }
    public onlyMeEditingThisResource(editors: User[]): boolean {
        if (!editors?.length) {
            return true;
        }
        return !!this.user?.id && editors.every((editor) => Number(editor.id) === Number(this.user.id));
    }
    /** Tooltip showing editor identity and the field they currently dominate. */
    public editorPresenceTooltip(editor: User): string {
        const role = editor.roleValueSpecs?.title ?? '';
        const base = role ? `${editor.name} - ${role}` : editor.name;
        if (!editor.dominated_field) {
            return base;
        }
        const label = this.fieldLabel(editor.dominated_field);
        return `${base} (${label})`;
    }

    private fieldLabel(field: string): string {
        const labels: { [key: string]: string } = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            bullets: $localize`:@@summary:Summary`,
            other_info: $localize`:@@otherInfo:Other info`,
            language_id: $localize`:@@language:Language`,
            tag_ids: $localize`:@@tags:Tags`,
            category_ids: $localize`:@@categories:Categories`,
            price: $localize`:@@price:Price`,
            currency: $localize`:@@currency:Currency`,
            instructions: $localize`:@@instructions:Instructions`,
        };
        return labels[field] ?? field;
    }
    public handleAccessLevelIndicator(res: Resource) {
        this.accessLevel = res.users_access_open
            ? 'all'
            : res.users_attached || res.groups_attached
            ? 'partial'
            : 'none';
        this.cdRef.markForCheck();
    }
    public onSubmit() {
        this.parentFormDirective.ngSubmit.emit(ResourceManagerEvent.SUBMIT);
    }
    resetForm() {
        FormHelper.resetForm(this.resourceForm);
        this.cdRef.markForCheck();
    }
    authorizeUsers() {
        this.host.openCollaborationSheet();
    }

    public specify() {
        this.bottomSheet
            .open(ResourceAvailabilitySheetComponent, {
                data: {
                    context: 'publish',
                    contextTitle: $localize`:@@specifyPublish:Specify Publish`,
                    contextIcon: 'publish_outline',
                    resource: this.resource,
                    title: this.resource.title,
                },
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .pipe(
                takeUntil(this.destroy),
                filter((result) => !!result)
            )
            .subscribe((value: Resource) => {
                this.message.openMessage(MessageSavedComponent);
                this.resource.users_access_open = value.users_access_open;
                this.handleAccessLevelIndicator(value);
                this.resourceForm.markAsDirty();
            });
    }

    public view(id: number) {
        this.router.navigate(['/home/dashboard/resource-details/' + id]);
    }
    public takeResource(resource: Resource) {
        resource.playableResource$
            .pipe(
                switchMap((resPlayableSpec) =>
                    this.resourceActionHandlerService.playResource({ resource, resPlayableSpec: resPlayableSpec })
                ),
                takeUntil(this.destroy),
                filter((res) => !!res && res.id == this.resource?.id)
            )
            .subscribe((res) => {
                if (res) {
                    this.resource.user_enrollment = res.user_enrollment;
                    this.cdRef.markForCheck();
                }
            });
    }

    public showTagResources(tag: ResourceTag) {
        this.showResources({ id: tag.id, label: tag.name, scope_id: AppScope.TAGS });
    }
    public showCatgeoryResources(category: ResourceCategory) {
        this.showResources({ id: category.id, label: category.title, scope_id: AppScope.CATEGORIES });
    }
    private showResources(item: GlobalSearchData) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: item.id,
                label: item.label,
                scope_id: item.scope_id,
            },
            panelClass: 'sheet-wrapper',
        });
    }
    createQuiz() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_TEST_FROM_MATERIAL,
            data: {
                resource: this.resource,
            },
        });
    }
    makeResourcePublic() {
        if (
            this.user.privileges.public == 'not_allowed' ||
            (this.user.privileges.public == 'approval_required' && !this.resourceForm.value.publish)
        ) {
            return;
        }
        this.resourcePublicationService
            .makeResourcePublic(this.resource)
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (!this.resource.publication) {
                    this.resource.publication = res.publication;
                    this.cdRef.markForCheck();
                }
            });
    }
    getPubToolTip() {
        if (this.user.privileges.public == 'not_allowed') {
            return $localize`You don't have the privilege required to public`;
        } else {
            if (this.resource?.publication?.approval_status) {
                switch (this.resource.publication.approval_status) {
                    case 'approved':
                        return $localize`Request to public is approved`;
                    case 'pending':
                        return $localize`Request to public is pending`;
                    case 'rejected':
                        return $localize`Request to public is rejected`;
                }
            } else {
                return $localize`Request to public`;
            }
        }
    }
    publishResource() {
        if (this.user.privileges.publish == 'not_allowed') {
            return;
        }
        this.resourcePublicationService
            .publishResource(this.resource)
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (!this.resource.publishment) {
                    this.resource.publishment = res.publishment;
                    this.cdRef.markForCheck();
                }
            });
    }
    getPublishToolTip() {
        if (this.user.privileges.publish == 'not_allowed') {
            return $localize`You don't have the privilege required to publish`;
        } else {
            if (this.resource?.publishment?.approval_status) {
                switch (this.resource.publishment.approval_status) {
                    case 'approved':
                        return $localize`Request to publish is approved`;
                    case 'pending':
                        return $localize`Request to publish is pending`;
                    case 'rejected':
                        return $localize`Request to publish is rejected`;
                }
            } else {
                return $localize`Request to publish`;
            }
        }
    }
    public checkEfficiency(type: ResourceTypes) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CHECK_EFFICIENCY,
            data: {
                icon: 'task_alt',
                title: $localize`Check before publish`,
                contentBody: $localize`Before you publish, make sure you review the quality of the ${ResourceTypes.label(
                    type,
                    { capitalize: false }
                )}. Some fields are mandatory and some is complimentary.
                You can improve the quality of your ${ResourceTypes.label(type, {
                    capitalize: false,
                })} by adding more information.`,
                positiveBtn: $localize`Ok`,
                mandatory: this.resKeysValidaty.filter((key) => key.mandatory),
                optional: this.resKeysValidaty.filter((key) => !key.mandatory),
            },
        });
    }
    public delete(resource: Resource) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete ${resource.title}`,
                contentBody: $localize`You are about to delete this ${ResourceTypes.label(this.typeId, {
                    capitalize: false,
                })}. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.resourceService.destroy(resource);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.message.openMessage(MessageDeletedComponent);
                    FormHelper.resetForm(this.resourceForm);
                    this.location.back();
                }
            });
    }
    public addResNote(resource: Resource) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.USER_NOTE,
            data: {
                privacy: null,
                resources: [resource],
                styleClass: 'playerTC',
                status: 'create',
            },
        });
    }
    public getLocalizedLabel(isSearchLabel: boolean) {
        return this.typeId === ResourceTypes.GeneralCoursePlan
            ? isSearchLabel
                ? $localize`Search authors`
                : $localize`Create a new author`
            : !isSearchLabel
            ? $localize`Create a new tutor`
            : $localize`Search tutors`;
    }
    public download(resource: Resource) {
        this.downloadHelper.download(resource.downloads);
    }

    public getMainEduTooltip() {
        return this.typeId === ResourceTypes.EVENT
            ? $localize`Main educator or organizer of the event.`
            : $localize`Main author`;
    }
    public list() {
        const resType = ResourceTypes.playerRoute(
            this.typeId,
            this.auth.getNK3PlatformValue() != 'user' && this.auth.getNK3PlatformValue() != 'guest'
        );
        this.router.navigate(
            this.auth.getNK3PlatformValue() == 'user'
                ? [`/home/manage/${resType}/list`]
                : [`/admin/content/${resType}/list`]
        );
    }
    public viewResLog(res: Resource) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.USER_PROGRESS,
            data: {
                resource: res,
                logs: this.activityLogService.getUserRelatedResourceActivities(
                    this.typeId === ResourceTypes.MATERIAL
                        ? { resource: res.id, eager: ['user_prompt'], sort: 'activity_id' }
                        : { resource: res.id }
                ),
            },
        });
    }
    viewScormPackageInfo(resource: Resource) {
        this.scormService
            .getScormByResId(resource.id)
            .pipe(
                takeUntil(this.destroy),
                map((scorm) => {
                    this.mobileService.updateSideNavSub({
                        dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
                        data: {
                            icon: 'business_center',
                            title: $localize`SCORM package info`,
                            contentBody: !scorm || !scorm?.id ? $localize`No SCORM package info` : null,
                            injectedTemplate: scorm ? this.scormPackageInfo : null,
                            templateContext: scorm ? { scorm } : null,
                        },
                    });
                })
            )
            .subscribe();
    }
    public viewRelatedResources(resourecs: ResourceData[]) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.RELATED_RESOURCES,
            data: {
                resources: resourecs,
            },
        });
    }
    public ngOnDestroy(): void {
        if (this.resource?.id) {
            this.resourceService.deleteCurrentResourceEditors(this.resource.id).subscribe();
        }
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    get isSaveDisabled(): boolean {
        // If builderForm is not present, only check resourceForm
        if (!this.resourceBuilder) {
            return !this.resourceForm.value.title || this.resourceForm.pristine;
        }
        // If both resourceForm and builderForm are present, check their states
        return (
            !this.resourceForm.value.title ||
            (this.resourceForm.pristine && this.resourceBuilder.getCurrentForm().pristine)
        );
    }
    specifyEditors() {
        this.host?.resourceComposer
            .editResourceAccessPermision('edit', 'asc', { eager: 'editors' })
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                this.resource.editors = res.editors;
                this.cdRef.markForCheck();
            });
    }

    /** Toggle whether an editor appears on the resource information page. */
    toggleEditorInfoVisibility(editor: User, show: boolean): void {
        if (!this.resource?.id || !editor?.id) {
            return;
        }

        const previousShow = editor.showsOnInfoPage;
        editor.metadata = { ...(editor.metadata ?? {}), show };
        this.cdRef.markForCheck();

        this.resourceService
            .updateResourceUserPermission(this.resource.id, editor.id, {
                permission_type: 'edit',
                metadata: { show },
            })
            .pipe(
                takeUntil(this.destroy),
                catchError(() => {
                    editor.metadata = { ...(editor.metadata ?? {}), show: previousShow };
                    this.cdRef.markForCheck();
                    return EMPTY;
                })
            )
            .subscribe();
    }
    public restoreResource(resource: Resource) {
        this.resourceService
            .restore(resource.id)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.message.openMessage(MessageSavedComponent);
                resource.deleted_at = null;
                this.cdRef.markForCheck();
            });
    }
    showMoreEditors() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'edit',
                title: $localize`Resource Editors`,
                injectedTemplate: this.resourceEditors,
                positiveBtn: $localize`Ok`,
            },
        });
    }
    public favoriteResource(resource: Resource) {
        resource.favorite = !resource.favorite;
        this.resourceService
            .update(
                {
                    id: resource.id,
                    favorite: resource.favorite,
                },
                true
            )
            .subscribe();
    }
}
